import { partials } from '@shopify/partial-rendering';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/standard-events';
import { transition } from 'view-transition';

/**
 * <s-live-form> — Wraps a <form> and re-fetches one or more partials on every input/change
 * event, keeping filter, sort, and search results in sync without a full page reload.
 *
 * Attributes (on <s-live-form>):
 *   partial    — Comma-separated list of {% partial 'name' %} region names to fetch/apply.
 *                Must exactly match the partial wrappers in templates/collection.liquid,
 *                templates/search.liquid, or blocks/_predictive-search.liquid — renaming
 *                either side silently breaks live updates.
 *   native-submit — If present, changes still request-submit the form, but the submit is
 *                   allowed to continue natively. Use for Shopify POST forms like localization.
 *   data-native-action — Optional absolute action URL applied just before native submission.
 *   debounce   — Debounce delay in ms for text `input` events (default 0 = immediate).
 *   data-updated-message — i18n string announced via #sr-announcer on success.
 *   data-error-message   — i18n string announced via #sr-announcer on fetch failure.
 *
 * Required child markup:
 *   form                      — The form element driving submissions; required.
 *   [name]                    — Any named input/select/checkbox whose values are serialized
 *                               into the request URL. `name` must match the URL param the
 *                               server-side Liquid template accepts (e.g. `q`, `sort_by`,
 *                               `filter.v.*`) — renaming breaks round-trip state rehydration.
 *   [data-sync-name]          — Optional visible proxy control that mirrors a named control
 *                               with the same URL param (used for mobile sort inside drawers).
 *
 * Update triggers:
 *   input event on non-select — Debounced by `debounce` ms; triggers form.requestSubmit().
 *   change on select/checkbox  — Immediate form.requestSubmit().
 *   form submit                — Remote-action forms: native navigation on user-initiated
 *                                submits; partial rendering on programmatic submits.
 *
 * Dispatches:
 *   s-live-form:updated — Bubbles after a successful partial apply.
 *
 * External coupling:
 *   aria-busy attribute (on self) — Set to "true" during fetch, removed on completion.
 *   history.replaceState          — URL updated with serialized form params (local forms only).
 *   #sr-announcer (global)        — Live region for success/error announcements.
 */
// The `partial` HTML attribute is a comma-separated list of {% partial 'name' %} region names
// in the server-rendered page. These strings are a naming contract: renaming a partial on either
// the Liquid side (templates/collection.liquid, templates/search.liquid,
// blocks/_predictive-search.liquid) or here will silently break partial-rendering updates.
class LiveForm extends HTMLElement {
  #timer;
  #programmatic = false;
  #ac;
  #abortController;
  #rafId;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const debounce = parseInt(this.getAttribute('debounce'), 10) || 0;
    const nativeSubmit = this.hasAttribute('native-submit');
    const form = this.querySelector('form');

    // Rehydration: each form input's `name` attribute must exactly match the URL param the
    // server-side Liquid template accepts (e.g. `q`, `sort_by`, `filter.v.*`).
    // Renaming any input `name` here, or the corresponding Liquid filter/sort param, breaks
    // round-trip state — the input will not be restored when the user navigates back.
    const params = new URLSearchParams(window.location.search);
    for (const el of this.querySelectorAll('[name]')) {
      if (el.type === 'checkbox') {
        const values = params.getAll(el.name);
        el.checked = values.includes(el.value);
      } else {
        let value = params.get(el.name);
        if (value != null) {
          // Clean the value by removing any URL parameters that might have leaked in
          value = value.split('?')[0].split('&')[0];
          if (value.trim()) {
            el.value = value;
          }
        }
      }
    }

    for (const el of this.querySelectorAll('[data-sync-name]')) {
      const name = el.dataset.syncName;
      if (!name) continue;
      let value = params.get(name);
      if (value == null) {
        value = this.#namedControls(name).find((control) => 'value' in control)?.value;
      }
      if (value != null) {
        value = String(value).split('?')[0].split('&')[0];
        if (value.trim()) {
          el.value = value;
        }
      }
    }

    // Single update path — all events funnel through form submit
    form.addEventListener('submit', (e) => {
      if (nativeSubmit) {
        this.#programmatic = false;
        if (this.dataset.nativeAction) {
          form.action = this.dataset.nativeAction;
        }
        return;
      }

      // For remote forms (action differs from current page), let user-initiated
      // submits (Enter key) navigate natively. Programmatic submits from
      // input/change handlers still do partial rendering.
      if (!this.#programmatic) {
        const action = form.getAttribute('action');
        const isRemote = action && new URL(action, location.origin).pathname !== location.pathname;
        if (isRemote) return;
      }
      this.#programmatic = false;
      e.preventDefault();
      this.#update();
    }, opts);

    // Text inputs: debounced
    this.addEventListener('input', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.dataset.syncName) this.#syncProxyControl(target);
      if (target.matches('select')) return;
      clearTimeout(this.#timer);
      if (debounce) {
        this.#timer = setTimeout(() => { this.#programmatic = true; form.requestSubmit(); }, debounce);
      } else {
        this.#programmatic = true;
        form.requestSubmit();
      }
    }, opts);

    // Selects & checkboxes: immediate
    this.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.dataset.syncName) this.#syncProxyControl(target);
      if (target.matches('[name]') && 'value' in target) {
        this.#syncProxyControls(target.getAttribute('name'), target.value);
      }
      if (target.matches('select, input[type="checkbox"]')) {
        clearTimeout(this.#timer);
        this.#programmatic = true;
        form.requestSubmit();
      }
    }, opts);

    // Clear button: resets the search input and re-triggers the input handler
    // (preserving the debounce/fetch flow). Scoped via `[data-clear-search]`
    // so consumers opt in declaratively instead of wiring inline onclick.
    this.addEventListener('click', (e) => {
      if (!e.target.closest('[data-clear-search]')) return;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, opts);

  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    clearTimeout(this.#timer);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #syncProxyControl(control) {
    const name = control.dataset.syncName;
    if (!name || !('value' in control)) return;
    for (const target of this.#namedControls(name)) {
      if ('value' in target) target.value = control.value;
    }
  }

  #syncProxyControls(name, value) {
    if (!name) return;
    for (const control of this.querySelectorAll('[data-sync-name]')) {
      if (control instanceof HTMLElement && control.dataset.syncName === name && 'value' in control) {
        control.value = value;
      }
    }
  }

  #namedControls(name) {
    return [...this.querySelectorAll('[name]')].filter((control) => control.getAttribute('name') === name);
  }

  async #update() {
    // Abort stale response so fast typing can't let an earlier result overwrite a fresher one
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController; // capture before any yield

    const form = this.querySelector('form');
    const action = form?.getAttribute('action');
    const isRemote = action && new URL(action, location.origin).pathname !== location.pathname;
    const url = isRemote ? new URL(action, location.origin) : new URL(window.location);

    // Clean up any existing form-related parameters first
    for (const el of this.querySelectorAll('[name]')) {
      url.searchParams.delete(el.name);
    }

    // Use FormData for proper serialization (handles checkboxes, multi-values)
    if (form) {
      const data = new FormData(form);
      for (const [name, value] of data) {
        const v = String(value).trim();
        if (v) url.searchParams.append(name, v);
      }
    } else {
      // Fallback for non-form elements
      for (const el of this.querySelectorAll('[name]')) {
        const value = el.value.trim();
        if (value) {
          url.searchParams.set(el.name, value);
        }
      }
    }

    const attr = this.getAttribute('partial');
    if (!attr) return;

    if (!isRemote) {
      // Let the browser paint before URL update
      await new Promise(resolve => setTimeout(resolve, 0));
      if (signal.aborted) return;
      history.replaceState(null, '', url);
    }

    this.setAttribute('aria-busy', 'true');

    const pendingEvent = this.#dispatchStandardEvent(url);

    try {
      const names = attr.split(/\s*,\s*/);
      const html = await partials.fetch(...names, { url: url.toString(), signal });
      await transition(() => partials.apply(html));

      this.removeAttribute('aria-busy');
      pendingEvent?.resolve();
      this.dispatchEvent(new CustomEvent('s-live-form:updated', { bubbles: true }));

      const announcer = document.getElementById('sr-announcer');
      if (announcer) {
        announcer.textContent = '';
        cancelAnimationFrame(this.#rafId);
        this.#rafId = requestAnimationFrame(() => {
          announcer.textContent = this.dataset.updatedMessage;
          this.#rafId = null;
        });
      }
    } catch (error) {
      pendingEvent?.reject(error);
      if (error.name === 'AbortError') return;
      this.removeAttribute('aria-busy');
      console.error('[s-live-form] Partial fetch failed:', error);
      const announcer = document.getElementById('sr-announcer');
      if (announcer) {
        announcer.textContent = '';
        cancelAnimationFrame(this.#rafId);
        this.#rafId = requestAnimationFrame(() => {
          announcer.textContent = this.dataset.errorMessage || '';
          this.#rafId = null;
        });
      }
    }
  }

  #dispatchStandardEvent(url) {
    const isCollection = this.dataset.collectionId && this.dataset.collectionHandle;
    const isPredictive = !isCollection && !!this.closest('s-predictive-search');

    if (isCollection) {
      const deferredPromise = CollectionUpdateEvent.createPromise();
      const productFilters = CollectionUpdateEvent.parseProductFilters(url.searchParams);

      this.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: this.dataset.collectionId,
            handle: this.dataset.collectionHandle,
            productsCount: this.#getResultsCount(),
          },
          ...(productFilters && { productFilters }),
          sortKey: CollectionUpdateEvent.getSortKey(url.searchParams),
          promise: deferredPromise.promise,
        })
      );

      return {
        resolve: () => deferredPromise.resolve({ productsCount: this.#getResultsCount() }),
        reject: (error) => deferredPromise.reject(error),
      };
    }

    const query = url.searchParams.get('q')?.trim() || '';
    if (!query) return;

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = isPredictive
      ? undefined
      : SearchUpdateEvent.parseProductFilters(url.searchParams);

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: isPredictive ? undefined : SearchUpdateEvent.getSortKey(url.searchParams),
        },
        promise: deferredPromise.promise,
      })
    );

    return {
      resolve: () => deferredPromise.resolve({ totalCount: this.#getResultsCount() }),
      reject: (error) => deferredPromise.reject(error),
    };
  }

  #getResultsCount() {
    const marker = this.querySelector('[data-results-count]');
    return Number(marker?.textContent || 0);
  }
}
customElements.define('s-live-form', LiveForm);
